import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import maintenanceService from "@services/maintenance.service";
import { MAINTENANCE_RECOVERY_POLL_MS, MAINTENANCE_STATUS_POLL_MS } from "@config/constants";
import { MAINTENANCE_STATUS_QUERY_KEY } from "@config/query";
import type { MaintenanceStatus } from "@app-types/maintenance.type";

interface UseMaintenanceStatusResult {
  status: MaintenanceStatus | null;
  isChecking: boolean;
}

/**
 * Single poll of the tools service, shared by the maintenance gate and the pre-alert.
 * Slow while nothing is happening; faster once maintenance is on, to let the user back in
 * as soon as the flag drops. Never retries: a failed poll resolves to `null` and the next
 * tick is seconds away.
 *
 * Cadence follows the last status the service actually answered with, not the last value
 * stored: an unreadable poll resolves to `null`, and forgetting that maintenance was on would
 * drop the user back to the slow interval for the rest of the outage.
 */
export function useMaintenanceStatus(): UseMaintenanceStatusResult {
  const wasActive = useRef(false);

  const {
    data = null,
    isPending,
    fetchStatus,
  } = useQuery({
    queryKey: [MAINTENANCE_STATUS_QUERY_KEY],
    queryFn: () => maintenanceService.getStatus(),
    refetchInterval: () =>
      wasActive.current ? MAINTENANCE_RECOVERY_POLL_MS : MAINTENANCE_STATUS_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: () => wasActive.current,
    staleTime: 0,
    retry: false,
  });

  if (data) wasActive.current = data.isActive;

  return { status: data, isChecking: isPending && fetchStatus === "fetching" };
}
